import React, { useEffect, useState } from "react";
import {
  Users, Building2, Tag, Activity, Clock, TrendingUp, AlertCircle,
  ArrowUpRight, RefreshCw, Sparkles, Wallet, FileWarning, ShieldCheck,
  CalendarClock, Award,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { adminStatsApi } from "@/lib/api/adminStats";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/lib/roles";

// ─── helpers ────────────────────────────────────────────────────────────────

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch (_) { return iso.slice(0, 10); }
}

// ─── sub-components ─────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, sub, accent = "text-primary", loading }) {
  return (
    <div className="surface-card p-5 flex items-start gap-4">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center bg-primary/10 shrink-0`}>
        <Icon className={`h-5 w-5 ${accent}`} />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground mb-1 truncate">{label}</div>
        {loading
          ? <div className="h-7 w-16 rounded bg-muted animate-pulse" />
          : <div className="text-2xl font-bold text-foreground leading-none">{fmt(value)}</div>
        }
        {sub && !loading && (
          <div className="text-xs text-muted-foreground mt-1">{sub}</div>
        )}
      </div>
    </div>
  );
}

const TYPE_COLORS = {
  CLINIC: "#6366f1",
  HOSPITAL: "#0ea5e9",
  PHARMACY: "#22c55e",
  LAB: "#f59e0b",
  RADIOLOGY_CENTER: "#ec4899",
  OTHER: "#94a3b8",
};

const TYPE_LABELS = {
  CLINIC: { en: "Clinic", ar: "عيادة" },
  HOSPITAL: { en: "Hospital", ar: "مستشفى" },
  PHARMACY: { en: "Pharmacy", ar: "صيدلية" },
  LAB: { en: "Lab", ar: "معمل" },
  RADIOLOGY_CENTER: { en: "Radiology", ar: "أشعة" },
  OTHER: { en: "Other", ar: "أخرى" },
};

function MiniTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-md text-xs">
      <div className="font-medium text-foreground mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-muted-foreground">
          <span className="h-2 w-2 rounded-full inline-block" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold text-foreground ml-1">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const OFFER_STATUS_COLORS = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  PENDING: "bg-amber-100 text-amber-700",
  INACTIVE: "bg-zinc-100 text-zinc-500",
  REJECTED: "bg-red-100 text-red-600",
};

// ─── main component ──────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const t = (en, ar) => lang === "ar" ? ar : en;

  const role = user?.role;
  const showFinance = role === ROLES.SUPER_ADMIN || role === ROLES.FINANCE_ADMIN;
  const showOperations = role === ROLES.SUPER_ADMIN || role === ROLES.OPS_ADMIN;

  const [stats, setStats] = useState(null);
  const [finance, setFinance] = useState(null);
  const [operations, setOperations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(false);
    try {
      const tasks = [adminStatsApi.summary()];
      if (showFinance)    tasks.push(adminStatsApi.finance().catch(() => null));
      if (showOperations) tasks.push(adminStatsApi.operations().catch(() => null));
      const [summary, ...rest] = await Promise.all(tasks);
      setStats(summary);
      let i = 0;
      if (showFinance)    setFinance(rest[i++]);
      if (showOperations) setOperations(rest[i++]);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = stats?.totals ?? {};
  const ts     = stats?.timeseries ?? { labels: [], members: [], visits: [] };
  const byType = stats?.providers_by_type ?? {};

  // Build chart data: last 14 days label-abbreviated
  const chartData = ts.labels.slice(-14).map((d, i) => ({
    day: d.slice(5),          // MM-DD
    members: ts.members.slice(-14)[i] ?? 0,
    visits:  ts.visits.slice(-14)[i]  ?? 0,
  }));

  // Bar chart: providers by type
  const typeData = Object.entries(byType).map(([k, v]) => ({
    name: TYPE_LABELS[k]?.[lang] || k,
    count: v,
    fill: TYPE_COLORS[k] || TYPE_COLORS.OTHER,
  }));

  return (
    <div className="max-w-7xl space-y-6 fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t("Dashboard", "لوحة التحكم")}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t("Platform overview & live metrics", "نظرة عامة على المنصة")}
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {t("Refresh", "تحديث")}
        </button>
      </div>

      {error && (
        <div className="surface-card p-4 flex items-center gap-3 border-destructive/30 bg-destructive/5">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
          <p className="text-sm text-destructive">
            {t("Failed to load stats. Check your connection and try again.", "فشل تحميل الإحصائيات.")}
          </p>
          <button onClick={() => load()} className="ml-auto text-xs underline text-destructive">
            {t("Retry", "إعادة المحاولة")}
          </button>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        <KpiCard
          icon={Users}
          label={t("Total Members", "إجمالي الأعضاء")}
          value={totals.members}
          sub={t(`${fmt(totals.active_members)} active`, `${fmt(totals.active_members)} نشط`)}
          loading={loading}
        />
        <KpiCard
          icon={Building2}
          label={t("Approved Providers", "مزودو الخدمة المعتمدون")}
          value={totals.approved_providers}
          sub={t(`${fmt(totals.providers)} total`, `${fmt(totals.providers)} إجمالي`)}
          loading={loading}
        />
        <KpiCard
          icon={Tag}
          label={t("Active Offers", "العروض النشطة")}
          value={totals.active_offers}
          sub={t(`${fmt(totals.offers)} total`, `${fmt(totals.offers)} إجمالي`)}
          loading={loading}
        />
        <KpiCard
          icon={Activity}
          label={t("Total Visits", "إجمالي الزيارات")}
          value={totals.visits}
          loading={loading}
        />
        <KpiCard
          icon={Clock}
          label={t("Pending KYC", "طلبات KYC المعلقة")}
          value={totals.pending_kyc}
          accent={totals.pending_kyc > 0 ? "text-amber-500" : "text-primary"}
          loading={loading}
        />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Line chart: new members + visits last 14 days */}
        <div className="surface-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-sm text-foreground">
              {t("Activity – last 14 days", "النشاط – آخر 14 يوم")}
            </h2>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-primary inline-block" />{t("Members", "الأعضاء")}</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-cyan-400 inline-block" />{t("Visits", "الزيارات")}</span>
            </div>
          </div>
          {loading
            ? <div className="h-48 rounded bg-muted animate-pulse" />
            : (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                  <Tooltip content={<MiniTooltip />} />
                  <Line type="monotone" dataKey="members" name={t("Members", "الأعضاء")} stroke="var(--primary)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="visits"  name={t("Visits", "الزيارات")} stroke="#22d3ee" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )
          }
        </div>

        {/* Bar chart: providers by type */}
        <div className="surface-card p-5">
          <h2 className="font-semibold text-sm text-foreground mb-4">
            {t("Providers by Type", "المزودون حسب النوع")}
          </h2>
          {loading
            ? <div className="h-48 rounded bg-muted animate-pulse" />
            : typeData.length === 0
              ? <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">{t("No data", "لا توجد بيانات")}</div>
              : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={typeData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: "var(--muted-foreground)" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                    <Tooltip content={<MiniTooltip />} />
                    <Bar dataKey="count" name={t("Count", "العدد")} radius={[4, 4, 0, 0]}>
                      {typeData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )
          }
        </div>
      </div>

      {/* Bottom row: recent members + recent offers */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recent Members */}
        <div className="surface-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-sm text-foreground">{t("Recent Members", "أحدث الأعضاء")}</h2>
            <a href="/admin/members" className="text-xs text-primary flex items-center gap-0.5 hover:underline">
              {t("View all", "عرض الكل")} <ArrowUpRight className="h-3 w-3" />
            </a>
          </div>
          {loading
            ? <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-8 rounded bg-muted animate-pulse" />)}</div>
            : (
              <div className="space-y-2">
                {(stats?.recent_members ?? []).map(m => (
                  <div key={m.id} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2.5">
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground leading-tight">{m.full_name || "—"}</div>
                        <div className="text-[10px] text-muted-foreground">{fmtDate(m.created_at, lang)}</div>
                      </div>
                    </div>
                    {m.tier && (
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold
                        ${m.tier === "PLATINUM" ? "bg-indigo-100 text-indigo-700"
                          : m.tier === "GOLD" ? "bg-amber-100 text-amber-700"
                          : "bg-zinc-100 text-zinc-700"}`}
                      >
                        {m.tier}
                      </span>
                    )}
                  </div>
                ))}
                {(stats?.recent_members ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground py-4 text-center">{t("No members yet", "لا يوجد أعضاء")}</p>
                )}
              </div>
            )
          }
        </div>

        {/* Recent Offers — same shell as Recent Members */}
        <div className="surface-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-sm text-foreground">{t("Recent Offers", "أحدث العروض")}</h2>
            <a href="/admin/offers" className="text-xs text-primary flex items-center gap-0.5 hover:underline">
              {t("View all", "عرض الكل")} <ArrowUpRight className="h-3 w-3" />
            </a>
          </div>
          {loading
            ? <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-8 rounded bg-muted animate-pulse" />)}</div>
            : (
              <div className="space-y-2">
                {(stats?.recent_offers ?? []).map(o => (
                  <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2.5">
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                        <Tag className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground leading-tight">
                          {lang === "ar" ? o.title_ar || o.title_en : o.title_en || o.title_ar || "—"}
                        </div>
                        <div className="text-[10px] text-muted-foreground">{fmtDate(o.created_at, lang)}</div>
                      </div>
                    </div>
                    {o.status && (
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${OFFER_STATUS_COLORS[o.status] || "bg-zinc-100 text-zinc-500"}`}>
                        {o.status}
                      </span>
                    )}
                  </div>
                ))}
                {(stats?.recent_offers ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground py-4 text-center">{t("No offers yet", "لا يوجد عروض")}</p>
                )}
              </div>
            )
          }
        </div>
      </div>

      {showFinance && finance && (
        <FinanceSection finance={finance} lang={lang} t={t} />
      )}

      {showOperations && operations && (
        <OperationsSection operations={operations} lang={lang} t={t} />
      )}
    </div>
  );
}

// ─── Finance section ─────────────────────────────────────────────────────────
function FinanceSection({ finance, lang, t }) {
  const cv = finance.claim_value || {};
  const sub = finance.subscription_revenue || {};
  const tierMix = finance.tier_mix || {};
  const top = finance.top_providers || [];
  const tierTotal = (tierMix.SILVER || 0) + (tierMix.GOLD || 0) + (tierMix.PLATINUM || 0);
  const tierPct = (k) => tierTotal ? Math.round(100 * (tierMix[k] || 0) / tierTotal) : 0;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <Wallet className="h-4 w-4 text-emerald-600" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {t("Finance", "المالية")}
        </h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Sparkles}
          accent="text-emerald-600"
          label={t("Redeemed value", "قيمة المستخدم")}
          value={`${fmt(cv.redeemed_value || 0)} EGP`}
          sub={t(`${cv.redeemed_count || 0} redeemed claims`, `${cv.redeemed_count || 0} عرض مستخدم`)}
        />
        <KpiCard
          icon={Tag}
          accent="text-amber-600"
          label={t("Estimated savings", "توفير تقديري")}
          value={`${fmt(cv.estimated_value || 0)} EGP`}
          sub={t(`${cv.claimed_count || 0} active claims`, `${cv.claimed_count || 0} حجز نشط`)}
        />
        <KpiCard
          icon={Wallet}
          accent="text-primary"
          label={t("New signups (30d)", "اشتراكات (٣٠ يوم)")}
          value={fmt(sub.new_members_30d || 0)}
          sub={t(`~${fmt(sub.monthly_signup_revenue_egp || 0)} EGP gross`, `~${fmt(sub.monthly_signup_revenue_egp || 0)} ج.م إجمالي`)}
        />
        <KpiCard
          icon={Award}
          accent="text-indigo-600"
          label={t("Total claim value", "إجمالي قيمة الحجوزات")}
          value={`${fmt(cv.total_value || 0)} EGP`}
          sub={t("incl. estimated", "شامل التقديري")}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Tier mix */}
        <div className="surface-card p-5">
          <h3 className="font-semibold text-sm mb-3">{t("Member tier mix", "توزيع مستويات الأعضاء")}</h3>
          {tierTotal === 0 ? (
            <p className="text-sm text-muted-foreground py-2">{t("No members yet", "لا يوجد أعضاء")}</p>
          ) : (
            <div className="space-y-3">
              {[
                { key: "SILVER",   label: lang === "ar" ? "فضي"     : "Silver",   color: "bg-zinc-400" },
                { key: "GOLD",     label: lang === "ar" ? "ذهبي"    : "Gold",     color: "bg-amber-400" },
                { key: "PLATINUM", label: lang === "ar" ? "بلاتيني" : "Platinum", color: "bg-indigo-500" },
              ].map(({ key, label, color }) => (
                <div key={key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-semibold">
                      {tierMix[key] || 0} <span className="text-muted-foreground font-normal">({tierPct(key)}%)</span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full ${color}`} style={{ width: `${tierPct(key)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top providers by claim activity */}
        <div className="surface-card p-5 lg:col-span-2">
          <h3 className="font-semibold text-sm mb-3">{t("Top providers by claim activity", "الأكثر نشاطاً")}</h3>
          {top.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">{t("No claims yet", "لا توجد حجوزات بعد")}</p>
          ) : (
            <div className="space-y-1.5">
              {top.slice(0, 5).map((p, i) => (
                <div key={p.provider_id || i} className="flex items-center gap-3 py-1.5 border-b border-border/40 last:border-0">
                  <div className="h-7 w-7 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.provider_name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {p.claim_count} {t("claims", "حجز")}
                      <span className="mx-1.5">•</span>
                      {p.redeemed_count} {t("redeemed", "مستخدم")}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-emerald-700">{fmt(p.value_total)} EGP</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Operations section ─────────────────────────────────────────────────────
function OperationsSection({ operations, lang, t }) {
  const f = operations.funnel || {};
  const kyc = operations.kyc_pipeline || {};
  const act = operations.activation_pipeline || {};
  const exp = operations.expiring || {};
  const rate = Math.round((f.redemption_rate || 0) * 100);

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {t("Operations", "العمليات")}
        </h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={TrendingUp}
          accent={rate >= 50 ? "text-emerald-600" : rate >= 25 ? "text-amber-600" : "text-rose-600"}
          label={t("Redemption rate", "معدل الاستخدام")}
          value={`${rate}%`}
          sub={t(`${f.redeemed || 0} of ${f.total_claims || 0} claims redeemed`, `${f.redeemed || 0} من ${f.total_claims || 0}`)}
        />
        <KpiCard
          icon={Tag}
          accent="text-primary"
          label={t("Active claims", "حجوزات نشطة")}
          value={fmt(f.claimed || 0)}
          sub={t("waiting for visit", "بانتظار الزيارة")}
        />
        <KpiCard
          icon={ShieldCheck}
          accent={kyc.pending > 0 ? "text-amber-600" : "text-emerald-600"}
          label={t("KYC awaiting review", "KYC قيد المراجعة")}
          value={fmt(kyc.pending || 0)}
          sub={t(`${kyc.approved || 0} approved`, `${kyc.approved || 0} معتمد`)}
        />
        <KpiCard
          icon={Users}
          accent={act.pending_review > 0 ? "text-amber-600" : "text-primary"}
          label={t("Members pending review", "أعضاء قيد المراجعة")}
          value={fmt(act.pending_review || 0)}
          sub={t(`${act.pending_payment || 0} awaiting payment`, `${act.pending_payment || 0} بانتظار الدفع`)}
        />
      </div>

      {/* Expiring lists */}
      <div className="grid lg:grid-cols-3 gap-4">
        <ExpiringCard
          title={t("Memberships expiring (30d)", "عضويات منتهية (٣٠ يوم)")}
          icon={CalendarClock}
          items={(exp.members || []).map(m => ({
            id: m.id,
            primary: m.full_name,
            secondary: `${m.member_code} · ${m.tier}`,
            days: m.days_to_expiry,
          }))}
          empty={t("No memberships expiring", "لا توجد عضويات منتهية")}
          lang={lang}
          t={t}
        />
        <ExpiringCard
          title={t("Offers expiring (14d)", "عروض منتهية (١٤ يوم)")}
          icon={Tag}
          items={(exp.offers || []).map(o => ({
            id: o.id,
            primary: lang === "ar" ? (o.title_ar || o.title_en) : (o.title_en || o.title_ar) || "—",
            secondary: o.provider_name,
            days: o.days_to_expiry,
          }))}
          empty={t("No offers expiring", "لا توجد عروض منتهية")}
          lang={lang}
          t={t}
        />
        <ExpiringCard
          title={t("Provider licenses expiring (30d)", "تراخيص منتهية (٣٠ يوم)")}
          icon={FileWarning}
          items={(exp.licenses || []).map(p => ({
            id: p.provider_id,
            primary: p.corporate_name,
            secondary: p.license_kind?.replace(/_/g, " "),
            days: p.days_to_expiry,
          }))}
          empty={t("No licenses expiring", "لا توجد تراخيص منتهية")}
          lang={lang}
          t={t}
        />
      </div>
    </section>
  );
}

function ExpiringCard({ title, icon: Icon, items, empty, lang, t }) {
  return (
    <div className="surface-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-amber-600 shrink-0" />
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">{empty}</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const days = item.days;
            const urgent = days != null && days <= 7;
            return (
              <div key={item.id} className="flex items-center gap-2 py-1 border-b border-border/40 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.primary}</p>
                  {item.secondary && (
                    <p className="text-[11px] text-muted-foreground truncate">{item.secondary}</p>
                  )}
                </div>
                <span className={`shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${urgent ? "bg-rose-100 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                  {days != null ? t(`${days}d`, `${days} يوم`) : "—"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
